import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles.css';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import { setPageMeta } from '../utils/pageMeta';
import heroImg from '../assets/programs/programs-hero.jpg';

// Kept alongside the same constant in Privacy.js rather than shared: the two
// documents can be revised independently, and a single shared date would
// silently backdate whichever one did not change.
const LAST_UPDATED = 'September 11, 2026';

export default function Terms() {
    useEffect(() => {
        setPageMeta(
            'Terms of Service - CHOPSTICKSTORY',
            'The terms that govern your use of the CHOPSTICKSTORY website, accounts, volunteer applications, and event registration.',
        );
        window.scrollTo({ top: 0 });
    }, []);

    return (
        <div className="legal-page">
            <PageHero image={heroImg} title="Terms of Service" />

            <main className="legal-main">
                <article className="legal-doc">
                    <p className="legal-doc__updated">
                        Last updated: {LAST_UPDATED}
                    </p>

                    <p className="legal-doc__lead">
                        These Terms of Service (the &ldquo;Terms&rdquo;) govern
                        your use of chopstickstory.org and the services offered
                        through it. CHOPSTICKSTORY is a California nonprofit
                        public benefit corporation and a 501(c)(3) tax-exempt
                        organization (EIN 99-0860654). By using this site, you
                        agree to these Terms.
                    </p>

                    <section aria-labelledby="terms-acceptance">
                        <h2 id="terms-acceptance">1. Acceptance</h2>
                        <p>
                            If you do not agree to these Terms, please do not
                            use the site. If you are using the site on behalf of
                            a school, group, or organization, you represent that
                            you are authorized to accept these Terms on its
                            behalf.
                        </p>
                    </section>

                    <section aria-labelledby="terms-eligibility">
                        <h2 id="terms-eligibility">2. Eligibility</h2>
                        <p>
                            You must be at least 13 years old to create an
                            account. If you are under 18, you may use the site
                            and apply to volunteer only with the knowledge and
                            permission of a parent or legal guardian, who
                            accepts these Terms on your behalf.
                        </p>
                    </section>

                    <section aria-labelledby="terms-accounts">
                        <h2 id="terms-accounts">3. Your account</h2>
                        <ul>
                            <li>
                                Give accurate information and keep it current.
                                Volunteer service records are issued from what
                                you enter.
                            </li>
                            <li>
                                Keep your password and your Google account
                                secure. You are responsible for activity under
                                your account. Tell us promptly at
                                info@chopstickstory.org if you suspect
                                unauthorized use.
                            </li>
                            <li>
                                One account per person. Do not create an account
                                for someone else or impersonate anyone.
                            </li>
                            <li>
                                You may delete your account at any time from
                                your profile page. Deletion is handled as
                                described in our{' '}
                                <Link to="/privacy">Privacy Policy</Link>.
                            </li>
                        </ul>
                        <p>
                            <strong>Google Sign-In.</strong> Signing in with
                            Google is optional and is subject to Google&rsquo;s
                            own terms. If you remove CHOPSTICKSTORY’s connection 
                            in your Google Account settings, you will need to grant 
                            consent again to sign in with Google. If you lose access 
                            to your Google account and cannot sign in another way, 
                            contact us for help.
                        </p>
                    </section>

                    <section aria-labelledby="terms-volunteer">
                        <h2 id="terms-volunteer">
                            4. Volunteer applications and service records
                        </h2>
                        <p>
                            Submitting a volunteer application is a request, not
                            an acceptance. We may approve or decline any
                            application at our discretion, and we may end a
                            volunteer placement at any time.
                        </p>
                        <p>
                            <strong>
                                Volunteering with CHOPSTICKSTORY is unpaid and
                                does not create an employment relationship,
                                partnership, or agency.
                            </strong>{' '}
                            Volunteers serve without expectation of wages or
                            benefits, consistent with California and federal law
                            governing volunteers for nonprofit organizations.
                        </p>
                        <p>
                            Service hours you log are reviewed before they are
                            certified. Submitting hours you did not serve, or
                            misrepresenting the work performed, is grounds for
                            rejecting the entry and closing your account. We may
                            confirm certified hours with a school or program
                            that asks us to verify them, at your request.
                        </p>
                        <p>
                            Participating in an in-person program may require
                            separate forms — a photo release, a liability
                            waiver, or a parental consent form. Those documents
                            govern in-person participation; these Terms govern
                            your use of the website.
                        </p>
                    </section>

                    <section aria-labelledby="terms-events">
                        <h2 id="terms-events">5. Events and registration</h2>
                        <p>
                            Event details, capacity, and schedules may change,
                            and an event may be cancelled. Registering does not
                            guarantee a seat where capacity is limited. We may
                            cancel a registration for no-shows, for repeated
                            late cancellations, or where conduct at a prior
                            event warranted it.
                        </p>
                    </section>

                    <section aria-labelledby="terms-conduct">
                        <h2 id="terms-conduct">6. Acceptable use</h2>
                        <p>You agree not to:</p>
                        <ul>
                            <li>
                                Break the law, or use the site to harass,
                                threaten, defame, or discriminate against
                                anyone.
                            </li>
                            <li>
                                Upload content that is obscene, hateful, or
                                infringes someone else&rsquo;s rights.
                            </li>
                            <li>
                                Probe, scan, or attempt to breach the site or
                                any account, or interfere with its normal
                                operation.
                            </li>
                            <li>
                                Scrape, crawl, or bulk-download site data, or
                                use automated means to create accounts or
                                registrations.
                            </li>
                            <li>
                                Misrepresent your identity, age, school, or
                                service hours.
                            </li>
                        </ul>
                    </section>

                    <section aria-labelledby="terms-content">
                        <h2 id="terms-content">7. Content you submit</h2>
                        <p>
                            You keep ownership of the text and images you
                            upload, such as your biography, profile photo, and
                            work-log descriptions. You grant CHOPSTICKSTORY a
                            non-exclusive, royalty-free license to store,
                            display, and reproduce that content as needed to
                            operate the site and administer our programs. This
                            license ends when you delete the content or your
                            account, except for copies we are required to keep.
                        </p>
                        <p>
                            You are responsible for having the rights to
                            whatever you upload, including permission from
                            anyone identifiable in a photograph. We may remove
                            content that violates these Terms.
                        </p>
                    </section>

                    <section aria-labelledby="terms-ip">
                        <h2 id="terms-ip">8. Our content</h2>
                        <p>
                            The CHOPSTICKSTORY name, logo, program names, site
                            design, text, photographs, and curriculum materials
                            are owned by CHOPSTICKSTORY or its licensors and are
                            protected by copyright and trademark law. You may
                            view and share the site for personal,
                            non-commercial purposes. Any other use — including
                            reproducing program materials or using our name or
                            logo to imply endorsement — requires our written
                            permission.
                        </p>
                    </section>

                    <section aria-labelledby="terms-donations">
                        <h2 id="terms-donations">9. Donations</h2>
                        <p>
                            Donations are processed by PayPal on PayPal&rsquo;s
                            site, subject to PayPal&rsquo;s terms. CHOPSTICKSTORY
                            is a 501(c)(3) organization and donations are
                            generally tax-deductible to the extent allowed by
                            law; we do not provide tax advice, so consult your
                            own advisor.
                        </p>
                        <p>
                            Donations are generally non-refundable. If you
                            believe a donation was made in error or without
                            authorization, email info@chopstickstory.org within
                            30 days and we will review it in good faith.
                        </p>
                    </section>

                    <section aria-labelledby="terms-third-party">
                        <h2 id="terms-third-party">
                            10. Third-party services and links
                        </h2>
                        <p>
                            The site relies on third-party providers — Google
                            for sign-in, PayPal for donations, and cloud
                            hosting, storage, and email providers — and links to
                            sites we do not control. We are not responsible for
                            the content, terms, or practices of those third
                            parties.
                        </p>
                    </section>

                    <section aria-labelledby="terms-disclaimer">
                        <h2 id="terms-disclaimer">11. Disclaimers</h2>
                        <p>
                            The site is provided &ldquo;as is&rdquo; and
                            &ldquo;as available,&rdquo; without warranties of
                            any kind, express or implied, including any implied
                            warranties of merchantability, fitness for a
                            particular purpose, and non-infringement. We do not
                            warrant that the site will be uninterrupted,
                            error-free, or secure, or that service-hour records
                            will satisfy any particular school or program
                            requirement.
                        </p>
                    </section>

                    <section aria-labelledby="terms-liability">
                        <h2 id="terms-liability">
                            12. Limitation of liability
                        </h2>
                        <p>
                            To the fullest extent permitted by law,
                            CHOPSTICKSTORY and its directors, officers,
                            employees, and volunteers will not be liable for any
                            indirect, incidental, special, consequential, or
                            punitive damages, or for lost data, arising out of
                            your use of the site. Our total liability for any
                            claim relating to the site will not exceed one
                            hundred U.S. dollars ($100).
                        </p>
                        <p>
                            Nothing in these Terms limits liability that cannot
                            be limited under California law, including liability
                            for fraud, willful injury, or violation of law under
                            California Civil Code § 1668.
                        </p>
                    </section>

                    <section aria-labelledby="terms-indemnity">
                        <h2 id="terms-indemnity">13. Indemnification</h2>
                        <p>
                            You agree to indemnify and hold CHOPSTICKSTORY
                            harmless from claims, losses, and reasonable
                            attorneys&rsquo; fees arising out of your misuse of
                            the site, your violation of these Terms, or your
                            infringement of someone else&rsquo;s rights.
                        </p>
                    </section>

                    <section aria-labelledby="terms-termination">
                        <h2 id="terms-termination">14. Suspension and termination</h2>
                        <p>
                            We may suspend or close an account that violates
                            these Terms or that we reasonably believe poses a
                            risk to other participants. You may stop using the
                            site and delete your account at any time. Sections
                            7 through 13 survive termination.
                        </p>
                    </section>

                    <section aria-labelledby="terms-law">
                        <h2 id="terms-law">
                            15. Governing law and disputes
                        </h2>
                        <p>
                            These Terms are governed by the laws of the State of
                            California, without regard to its conflict-of-laws
                            rules. Before filing anything, please email
                            info@chopstickstory.org so we can try to resolve the
                            issue directly. If that does not work, you and
                            CHOPSTICKSTORY agree that the state and federal
                            courts located in Santa Clara County, California
                            have exclusive jurisdiction, and each of us consents
                            to venue there. Nothing here prevents either of us
                            from bringing a claim in small claims court.
                        </p>
                    </section>

                    <section aria-labelledby="terms-changes">
                        <h2 id="terms-changes">16. Changes to these Terms</h2>
                        <p>
                            We may revise these Terms. When we do, we will
                            update the &ldquo;Last updated&rdquo; date above,
                            and for material changes we will post a notice on
                            the site or email account holders before the change
                            takes effect. Continuing to use the site after that
                            means you accept the revised Terms.
                        </p>
                    </section>

                    <section aria-labelledby="terms-misc">
                        <h2 id="terms-misc">17. General</h2>
                        <p>
                            If any provision of these Terms is held
                            unenforceable, the rest stays in effect. Our failure
                            to enforce a provision is not a waiver of it. You
                            may not assign these Terms; we may assign them in
                            connection with a merger or transfer of assets.
                            These Terms, together with the{' '}
                            <Link to="/privacy">Privacy Policy</Link>, are the
                            entire agreement between you and CHOPSTICKSTORY
                            regarding the site.
                        </p>
                    </section>

                    <section aria-labelledby="terms-contact">
                        <h2 id="terms-contact">18. Contact us</h2>
                        <address>
                            CHOPSTICKSTORY
                            <br />
                            20570 Shady Oak Ln, Cupertino, CA
                            <br />
                            <a href="mailto:info@chopstickstory.org">
                                info@chopstickstory.org
                            </a>
                            <br />
                            (607) 973-0750
                        </address>
                    </section>
                </article>
            </main>

            <Footer />
        </div>
    );
}
